import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Star, ChevronLeft, ChevronRight, Users, Camera, Loader2, Send, Pencil, Trash2, X, ImageOff
} from "lucide-react";

const WellnessMealReview = () => {
  const { token } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedbackState, setFeedbackState] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [expandedPhoto, setExpandedPhoto] = useState(null);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const res = await fetch(`${API}/corporate/dietitian/programs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const d = await res.json();
          setPrograms(d.programs || []);
          if (d.programs?.length > 0) setSelectedProgram(d.programs[0].program_id);
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchPrograms();
  }, [token]);

  const refreshMeals = async () => {
    if (!selectedProgram) return;
    try {
      const res = await fetch(`${API}/corporate/dietitian/programs/${selectedProgram}/meals?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (!selectedProgram) return;
    setLoading(true);
    refreshMeals().finally(() => setLoading(false));
  }, [selectedProgram, date, token]);

  const changeDate = (offset) => {
    const d = new Date(date);
    d.setDate(d.getDate() + offset);
    setDate(d.toISOString().split("T")[0]);
  };

  const setFb = (id, field, value) => {
    setFeedbackState(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const startEditing = (id, feedback) => {
    setFeedbackState(prev => ({
      ...prev, [id]: { rating: feedback.rating, comment: feedback.comment || "", editing: true }
    }));
  };

  const cancelEditing = (id) => {
    setFeedbackState(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  const submitFeedback = async (photoId) => {
    const fb = feedbackState[photoId];
    if (!fb?.rating) { toast.error("Please select a rating"); return; }
    setFb(photoId, "submitting", true);
    try {
      const res = await fetch(`${API}/meal-photos/${photoId}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rating: fb.rating, comment: fb.comment || "" })
      });
      if (res.ok) {
        toast.success(fb.editing ? "Feedback updated!" : "Feedback sent!");
        await refreshMeals();
        cancelEditing(photoId);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed");
      }
    } catch { toast.error("Error submitting feedback"); }
    finally { setFb(photoId, "submitting", false); }
  };

  const deleteFeedback = async (photoId) => {
    setDeletingId(photoId);
    try {
      const res = await fetch(`${API}/meal-photos/${photoId}/feedback`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Feedback deleted");
        await refreshMeals();
        cancelEditing(photoId);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed");
      }
    } catch { toast.error("Error deleting feedback"); }
    finally { setDeletingId(null); }
  };

  const StarRating = ({ id }) => {
    const fb = feedbackState[id] || {};
    const rating = fb.rating || 0;
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((s) => (
          <button key={s} onClick={() => setFb(id, "rating", s)}
            className="p-0.5 transition-transform hover:scale-125" data-testid={`star-${id}-${s}`}>
            <Star className={`w-6 h-6 ${s <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300 hover:text-yellow-200"}`} />
          </button>
        ))}
      </div>
    );
  };

  const photos = data?.photos || [];
  const participantsWithPhotos = [...new Set(photos.map(p => p.user_id))].length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-secondary" data-testid="wellness-review-title">Wellness Meal Review</h2>
          <p className="text-sm text-muted-foreground">Review and rate meal photos from your wellness participants</p>
        </div>
        {programs.length > 0 && (
          <Select value={selectedProgram} onValueChange={setSelectedProgram}>
            <SelectTrigger className="w-[220px] rounded-xl" data-testid="review-program-select">
              <SelectValue placeholder="Select program" />
            </SelectTrigger>
            <SelectContent>
              {programs.map((p) => (
                <SelectItem key={p.program_id} value={p.program_id}>{p.name} ({p.organization_name})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Date Navigation */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => changeDate(-1)} className="rounded-full">
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="rounded-xl w-[160px] text-center font-medium" data-testid="review-date-picker" />
        <Button variant="ghost" size="sm" onClick={() => changeDate(1)} className="rounded-full"
          disabled={date >= new Date().toISOString().split("T")[0]}>
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      {/* Stats */}
      {data && (
        <div className="flex gap-4 text-sm">
          <Badge className="bg-emerald-100 text-emerald-700 gap-1.5 py-1.5 px-3 rounded-xl">
            <Users className="w-3.5 h-3.5" />
            {participantsWithPhotos}/{data.total_participants} uploaded
          </Badge>
          <Badge className="bg-blue-100 text-blue-700 gap-1.5 py-1.5 px-3 rounded-xl">
            <Camera className="w-3.5 h-3.5" />
            {photos.length} photos
          </Badge>
        </div>
      )}

      {/* Photo Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="animate-pulse h-64 bg-white rounded-2xl" />)}
        </div>
      ) : photos.length === 0 ? (
        <Card className="rounded-2xl p-10 text-center border-emerald-100">
          <Camera className="w-14 h-14 text-emerald-200 mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">No meal photos uploaded for this date</p>
          <p className="text-xs text-muted-foreground mt-1">Participants will snap and upload their meals throughout the day</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {photos.map((photo) => {
            const hasFeedback = !!photo.feedback;
            const fb = feedbackState[photo.photo_id] || {};
            const isEditing = fb.editing === true;
            const showForm = isEditing || !hasFeedback;

            return (
              <Card key={photo.photo_id} className={`rounded-2xl border overflow-hidden ${hasFeedback && !isEditing ? "border-emerald-200" : "border-orange-100/50"}`}
                data-testid={`photo-card-${photo.photo_id}`}>
                {/* Photo Image */}
                <div className="relative cursor-pointer" onClick={() => setExpandedPhoto(expandedPhoto === photo.photo_id ? null : photo.photo_id)}>
                  {photo.image_url ? (
                    <img src={photo.image_url} alt={photo.caption || "Meal"}
                      className={`w-full object-cover transition-all ${expandedPhoto === photo.photo_id ? "max-h-[400px]" : "h-48"}`} />
                  ) : (
                    <div className="h-48 bg-gray-100 flex items-center justify-center">
                      <ImageOff className="w-10 h-10 text-gray-300" />
                    </div>
                  )}
                  {/* Meal type badge overlay */}
                  <Badge className="absolute top-2 left-2 bg-black/60 text-white text-[10px] capitalize">
                    {photo.meal_type || "meal"}
                  </Badge>
                  <span className="absolute top-2 right-2 text-[10px] bg-black/60 text-white px-2 py-0.5 rounded-full">
                    {new Date(photo.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>

                <CardContent className="p-4 space-y-3">
                  {/* Participant + Caption */}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-secondary text-sm">{photo.participant_name}</span>
                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{photo.participant_alias}</Badge>
                    </div>
                    {photo.caption && <p className="text-sm text-muted-foreground mt-1">{photo.caption}</p>}
                  </div>

                  {/* Existing Feedback Display */}
                  {hasFeedback && !isEditing && (
                    <div className="bg-emerald-50 rounded-xl p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map(s => (
                              <Star key={s} className={`w-4 h-4 ${s <= photo.feedback.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`} />
                            ))}
                          </div>
                          <span className="text-[10px] text-muted-foreground">by {photo.feedback.physician_name}</span>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-blue-600 hover:bg-blue-50"
                            data-testid={`edit-photo-feedback-${photo.photo_id}`}
                            onClick={() => startEditing(photo.photo_id, photo.feedback)}>
                            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-red-500 hover:bg-red-50"
                            data-testid={`delete-photo-feedback-${photo.photo_id}`}
                            disabled={deletingId === photo.photo_id}
                            onClick={() => deleteFeedback(photo.photo_id)}>
                            {deletingId === photo.photo_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
                            Delete
                          </Button>
                        </div>
                      </div>
                      {photo.feedback.comment && <p className="text-sm italic text-secondary">"{photo.feedback.comment}"</p>}
                    </div>
                  )}

                  {/* Feedback Form */}
                  {showForm && (
                    <div className="space-y-2 bg-orange-50/50 rounded-xl p-3 border border-orange-100/50">
                      <div className="flex items-center justify-between">
                        <StarRating id={photo.photo_id} />
                        <span className="text-xs text-muted-foreground">{isEditing ? "Update" : "Rate this meal"}</span>
                      </div>
                      <Textarea
                        placeholder="Leave a comment for encouragement..."
                        value={fb.comment || ""}
                        onChange={(e) => setFb(photo.photo_id, "comment", e.target.value)}
                        className="rounded-xl text-sm min-h-[60px] resize-none"
                        data-testid={`comment-${photo.photo_id}`}
                      />
                      <div className="flex gap-2 justify-end">
                        {isEditing && (
                          <Button size="sm" variant="ghost" onClick={() => cancelEditing(photo.photo_id)}
                            className="rounded-xl text-muted-foreground" data-testid={`cancel-edit-${photo.photo_id}`}>
                            <X className="w-4 h-4 mr-1" /> Cancel
                          </Button>
                        )}
                        <Button size="sm" disabled={!fb.rating || fb.submitting}
                          onClick={() => submitFeedback(photo.photo_id)}
                          className="bg-emerald-600 hover:bg-emerald-700 rounded-xl"
                          data-testid={`submit-feedback-${photo.photo_id}`}>
                          {fb.submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                          {isEditing ? "Update" : "Send"}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WellnessMealReview;
